import { Component, OnDestroy, Input, ChangeDetectionStrategy, EventEmitter, Output } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProductSetting } from '../classes/productsetting';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxoFilterRowModule, DxoHeaderFilterModule, DxoEditingModule, DxoPagerModule, DxoPagingModule, DxoSelectionModule, DxoScrollingModule, DxoColumnChooserModule, DxoExportModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule, DxLookupModule, DxTextBoxModule } from 'devextreme-angular';
import { NgIf, AsyncPipe } from '@angular/common';
import { TooltipCircleComponent } from 'libs/application-link/shared/src/lib/components/tooltip-circle.component';
import { CallContext } from '../classes/call-context';
import { HandlingModeOption } from '../classes/handlingModeOption.class';
import { AssistanceModalComponent } from './assistance-modal.component';

@Component({
    selector: 'productsettings-modal',
    templateUrl: 'productsettingsmodal.component.html',
    styleUrls: ['../styles/csp-styles.scss', 'productsettingsmodal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgIf,
        DxDataGridModule,
        DxoFilterRowModule,
        DxoHeaderFilterModule,
        DxoEditingModule,
        DxoPagerModule,
        DxoPagingModule,
        DxoSelectionModule,
        DxoScrollingModule,
        DxoColumnChooserModule,
        DxoExportModule,
        DxiColumnModule,
        DxTemplateModule,
        DxLookupModule,
		DxTextBoxModule,
        AsyncPipe,
		TooltipCircleComponent
    ],
})
export class ProductSettingsModalComponent implements OnDestroy {
	@Output()
	promptRefresh = new EventEmitter<boolean>();

	@Input()
	handlingModeOptions: HandlingModeOption[];

	@Input()
	translations;

	@Input()
	cc: CallContext;

	@Input()
	hasProductSettingCategories: boolean;

	@Input()
	assistanceModal: AssistanceModalComponent;

	visible$ = new BehaviorSubject<boolean>(false);

	loading$ = new BehaviorSubject<boolean>(false);

	destroy$ = new Subject<void>();

	productSettings$ = new BehaviorSubject<ProductSetting[]>(undefined);

	categories: string[];

	tl;

	constructor() {}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.hide();
	}

	public onContainerClicked(event: MouseEvent): void {
		if ((<HTMLElement>event.target).classList.contains('modal')) {
			this.hide();
		}
	}

	public hide(): void {
		this.visible$.next(false);
	}

	public show() {
		this.tl = this.translations;

		this.productSettings$.next(undefined);
		this.getProductSettings().pipe(takeUntil(this.destroy$)).subscribe({
			next: pss => {
				const ownPss = pss.filter(ps => ps.linkId !== 0);
				this.categories = [];
				for(const ps of ownPss) {
					const defaultPs = pss.find(ps2 => ps.productCode === ps2.productCode && ps2.linkId === 0);
					if(defaultPs) {
						const foundOption = this.handlingModeOptions.find(hmo => hmo.id === defaultPs.handlingMode)
						ps.defaultHandlingMode = foundOption.defaultDescription ?? foundOption.description;
					}
					if(!this.categories.includes(ps.category || '')) this.categories.push(ps.category || '');
				}
				this.productSettings$.next(ownPss);
			},
			error: err => this.assistanceModal.showError(`Failed to retrieve product settings.`, err)
		});

		this.visible$.next(true);
	}

	onCategoryValueChanged(value, cellInfo) {
		cellInfo.setValue(value);
		this.categories.length = 0;
		for(const ps of this.productSettings$.value) {
			if(!this.categories.includes(ps.category)) this.categories.push(ps.category);
		}
	}

	public cancel(): void {
		this.hide();
	}

	public ok() {
		this.loading$.next(true);

		this.updateProductSettings(this.productSettings$.value).pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.loading$.next(false);

				this.hide();

				this.promptRefresh.next(true);
			},
			error: err => {
				this.loading$.next(false);

				this.hide();

				this.assistanceModal.showError(`Failed to update product settings.`, err);
			}
		});
	}

	getProductSettings() {
		return this.cc.http.get<ProductSetting[]>(`Common/GetProductSettings`, this.cc.getHeaders);
	}

	updateProductSettings(productSettings: ProductSetting[]) {
		return this.cc.http.post(`Common/UpdateProductSettings`, productSettings, this.cc.postHeaders);
	}
}
